import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  Directive,
  ElementRef,
  Injector,
  afterNextRender,
  effect,
  inject,
} from '@angular/core';

import { DIALOG_DATA, DialogRef, DialogService, type OpenDialog } from '@services/dialog.service';

@Directive({ selector: 'dialog[appTopLayer]' })
class TopLayer {
  private readonly element = inject<ElementRef<HTMLDialogElement>>(ElementRef);

  constructor() {
    afterNextRender(() => {
      const dialog = this.element.nativeElement;
      if (!dialog.open) dialog.showModal();
    });
  }
}

@Component({
  selector: 'app-dialog-host',
  imports: [NgComponentOutlet, TopLayer],
  template: `
    @for (dialog of dialogService.dialogs(); track dialog.id) {
      <dialog
        appTopLayer
        class="modal-host"
        [attr.aria-label]="dialog.label"
        (close)="dialog.ref.close()"
        (click)="onBackdropClick($event, dialog)"
      >
        <div
          class="modal-panel"
          [class.modal-panel--sm]="dialog.size === 'sm'"
          [class.modal-panel--lg]="dialog.size === 'lg'"
          [class.modal-panel--xl]="dialog.size === 'xl'"
        >
          <ng-container
            [ngComponentOutlet]="dialog.component"
            [ngComponentOutletInjector]="injectorFor(dialog)"
          />
        </div>
      </dialog>
    }
  `,
})
export class DialogHost {
  protected readonly dialogService = inject(DialogService);

  private readonly parentInjector = inject(Injector);
  private readonly injectors = new Map<number, Injector>();

  constructor() {
    effect(() => {
      const open = this.dialogService.dialogs();

      document.body.style.overflow = open.length > 0 ? 'hidden' : '';

      const live = new Set(open.map((dialog) => dialog.id));
      for (const id of this.injectors.keys()) {
        if (!live.has(id)) this.injectors.delete(id);
      }
    });
  }

  protected injectorFor(dialog: OpenDialog): Injector {
    let injector = this.injectors.get(dialog.id);

    if (!injector) {
      injector = Injector.create({
        parent: this.parentInjector,
        providers: [
          { provide: DIALOG_DATA, useValue: dialog.data },
          { provide: DialogRef, useValue: dialog.ref },
        ],
      });
      this.injectors.set(dialog.id, injector);
    }

    return injector;
  }

  protected onBackdropClick(event: MouseEvent, dialog: OpenDialog): void {
    if (event.target === event.currentTarget) {
      dialog.ref.close();
    }
  }
}
